import { createSlice, PayloadAction, nanoid } from "@reduxjs/toolkit";
import { resetAllState } from "./resetAllState";
import { NativeNotificationState } from "../../interfaces/NotificationObject";

// A single in-session notification (transient — not persisted, per the agreed
// architecture). This slice is the single source of truth that every UI surface
// reads from: bell badges, toasts, per-tab dots all derive from here.
export interface NotificationItem {
  id: string;
  type: string; // "new_message" | "product_question" | "new_review" | ...
  title: string;
  body: string;
  data: Record<string, string>; // ids used for deep-link routing
  receivedAt: number;
  read: boolean;
}

type PermissionState = NotificationPermission | "unsupported";

// Cap the in-session notification list so it can't grow unbounded.
const MAX_ITEMS = 50;

interface NotificationsState {
  items: NotificationItem[];
  unreadCount: number;
  permission: PermissionState;
  // Native (Android app) notification permission, mirrored from the bridge.
  nativePermission: NativeNotificationState;
}

const initialState: NotificationsState = {
  items: [],
  unreadCount: 0,
  permission:
    typeof Notification !== "undefined" ? Notification.permission : "unsupported",
  nativePermission: "unknown",
};

const notificationsSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    notificationReceived: {
      reducer(state, action: PayloadAction<NotificationItem>) {
        state.items.unshift(action.payload);
        if (state.items.length > MAX_ITEMS) {
          state.items.length = MAX_ITEMS;
        }
        if (!action.payload.read) state.unreadCount += 1;
      },
      prepare(input: {
        type: string;
        title: string;
        body: string;
        data?: Record<string, string>;
      }) {
        return {
          payload: {
            id: nanoid(),
            type: input.type,
            title: input.title,
            body: input.body,
            data: input.data ?? {},
            receivedAt: Date.now(),
            read: false,
          } as NotificationItem,
        };
      },
    },
    markRead(state, action: PayloadAction<string>) {
      const item = state.items.find((i) => i.id === action.payload);
      if (item && !item.read) {
        item.read = true;
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    },
    markAllRead(state) {
      state.items.forEach((i) => (i.read = true));
      state.unreadCount = 0;
    },
    clearNotifications(state) {
      state.items = [];
      state.unreadCount = 0;
    },
    setPermission(state, action: PayloadAction<PermissionState>) {
      state.permission = action.payload;
    },
    setNativePermission(
      state,
      action: PayloadAction<NativeNotificationState>
    ) {
      state.nativePermission = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetAllState, () => ({
      ...initialState,
      permission:
        typeof Notification !== "undefined"
          ? Notification.permission
          : "unsupported",
    }));
  },
});

export const {
  notificationReceived,
  markRead,
  markAllRead,
  clearNotifications,
  setPermission,
  setNativePermission,
} = notificationsSlice.actions;

export default notificationsSlice.reducer;
